/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-01-27 11:43:06
 */
import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useAppSelector, useVip, useVipKind, usePayment } from '../../app/hooks'
import type { Member } from './constants'
import CONFIG from '../../constants/config'
import { TrackingManager } from './TrackingManager'
const VIP_KIND = CONFIG.VIP_KIND
/**
 * 会员等级列表权重映射：数值越大等级越高
 * VIP(1): 基础会员
 * VVIP(3): 高级会员
 * SVIP(2): 超级会员
 * EVIP(4): 企业会员
 */

const KIND_WEIGHTS: Record<number, number> = {
  0: 0, // 免费版
  [VIP_KIND.VIP]: 1,
  [VIP_KIND.VVIP]: 2,
  [VIP_KIND.SVIP]: 3,
  [VIP_KIND.EVIP]: 4
}

export const useAvailableVipKinds = () => {
  const { isVip } = useVip()
  const vipKind = useVipKind()
  const groupState = useAppSelector((state) => state.group)

  const avaiableVipKinds = useMemo(() => {
    // 如果未开通会员，权重为0
    const currentWeight = (isVip && vipKind && KIND_WEIGHTS[vipKind]) || 0

    // 返回所有权重大于等于当前等级的会员类型，并按权重升序排列
    return Object.values(VIP_KIND)
      .filter((kind) => {
        const weight = KIND_WEIGHTS[kind]
        return weight && weight >= currentWeight
      })
      .sort((a, b) => KIND_WEIGHTS[a] - KIND_WEIGHTS[b])
  }, [isVip, vipKind, VIP_KIND])

  /**
   * 默认选中的会员等级
   * 如果升级会员等级列表为空，默认选中基础会员
   */
  const defaultSelected = useMemo(() => {
    const topNeedVipKind =
      groupState.statistics?.topNeedVipKind || 0
    if (topNeedVipKind) {
      return topNeedVipKind === VIP_KIND.EVIP ? VIP_KIND.SVIP : topNeedVipKind
    }
    if (isVip) {
      // avaiableVipKinds[0]是当前会员等级，推荐升级为下一级会员，暂不支持企业级会员的开通，兜底为当前会员等级
      if (!avaiableVipKinds[1] || avaiableVipKinds[1] === VIP_KIND.EVIP) {
        return vipKind
      } else {
        return avaiableVipKinds[1]
      }
    } else {
      return VIP_KIND.VIP
    }
  }, [isVip, vipKind, avaiableVipKinds, groupState])

  return { avaiableVipKinds, defaultSelected }
}

export const useCanUpgrade = (member: Member) => {
  const vipKind = useVipKind()
  return useMemo(() => {
    const currentWeight = vipKind !== undefined ? KIND_WEIGHTS[vipKind] : 0
    const memberWeight = KIND_WEIGHTS[member.vipKind] ?? 0
    return currentWeight > memberWeight
  }, [vipKind, member.vipKind])
}

export const useVipTypeDisabled = (member: Member) => {
  const { avaiableVipKinds } = useAvailableVipKinds()
  return useMemo(() => {
    return !avaiableVipKinds.includes(member.vipKind)
  }, [avaiableVipKinds])
}

export const useButtonText = (member: Member) => {
  const { isVip } = useVip()
  const vipKind = useVipKind()
  const { avaiableVipKinds } = useAvailableVipKinds()
  const canUpgrade = useCanUpgrade(member)
  const isFreeVersion = member.vipKind === 0
  return useMemo(() => {
    if (isVip) {
      if (member.vipKind === vipKind) {
        return '立即续费'
      }
      if (member.vipKind === CONFIG.VIP_KIND.EVIP) {
        return '立即咨询'
      }
      if (
        !isFreeVersion &&
        avaiableVipKinds.every(
          (kind) => KIND_WEIGHTS[kind] > KIND_WEIGHTS[member.vipKind]
        )
      ) {
        return '低于当前等级'
      }
      if (canUpgrade) {
        return '立即升级'
      }
    }
    return member.buttonText
  }, [isFreeVersion, isVip, member, avaiableVipKinds, vipKind])
}

export const usePrivilegeTracking = () => {
  const manager = useRef(TrackingManager.getInstance()).current
  const [state, setState] = useState(manager.getState())
  const { paymentStatus } = usePayment()

  // 监听支付弹窗状态，控制计时器暂停/恢复
  useEffect(() => {
    if (paymentStatus.visible) {
      manager.pauseAll()
    } else {
      manager.resumeAll()
    }
  }, [paymentStatus.visible, manager])

  // 存储已注册的元素引用，用于在组件卸载时清理
  const nodeMap = useRef<Map<string, HTMLElement>>(new Map()).current
  const refHandlers = useRef<Map<string, (node: HTMLElement | null) => void>>(
    new Map()
  ).current

  useEffect(() => {
    const unsubscribe = manager.subscribe(setState)
    // 组件卸载时，清理所有已注册的元素引用
    return () => {
      unsubscribe()
      nodeMap.forEach((node) => manager.unregisterRef(node))
      nodeMap.clear()
      refHandlers.clear()
      TrackingManager.releaseInstance()
    }
  }, [manager, nodeMap, refHandlers])

  const registerRef = useCallback(
    (category: string) => {
      if (!refHandlers.has(category)) {
        refHandlers.set(category, (node: HTMLElement | null) => {
          if (node) {
            manager.registerRef(category, node)
            nodeMap.set(category, node)
          } else {
            const oldNode = nodeMap.get(category)
            if (oldNode) {
              manager.unregisterRef(oldNode)
              nodeMap.delete(category)
            }
          }
        })
      }
      return refHandlers.get(category)!
    },
    [manager, nodeMap, refHandlers]
  )

  return {
    ...state,
    registerRef,
    pauseAll: () => manager.pauseAll(),
    resumeAll: () => manager.resumeAll(),
    onInteraction: () => manager.notifyInteraction()
  }
}
